import { singularSdk } from 'singular-sdk';

const SDID_POLL_MS = 100;
/**
 * Max wait before checkout proceeds without SDID.
 * Kept short on web so the click → Stripe redirect path is not blocked for
 * multi-second stretches when Singular never yields an ID (ad blockers, failed init).
 */
const SDID_POLL_TIMEOUT_MS = 500;

/**
 * Resolve the Singular device ID for Stripe checkout attribution.
 * Only available in production (SDK is not initialized elsewhere).
 *
 * Best-effort fail-open behavior:
 * - null / empty / whitespace-only SDID: keep polling until timeout
 * - per-call throws from `getSingularDeviceId`: treat as miss, keep polling
 *   (SDK may throw transiently during init); log once if still unresolved
 * - unexpected outer failure: log and return undefined
 * - after timeout with no SDID: return undefined so checkout still proceeds
 */
export async function getWebSingularSdid(): Promise<string | undefined> {
  if (process.env.REACT_APP_TRIMBOX_ENV !== 'production') {
    return undefined;
  }

  let lastError: unknown;
  const startedAt = Date.now();

  try {
    let hasTimeLeft = true;
    while (hasTimeLeft) {
      try {
        const sdid = singularSdk.getSingularDeviceId()?.trim();
        if (sdid) {
          return sdid;
        }
      } catch (e) {
        // Fail-open per attempt: do not abort the whole poll on a transient throw.
        lastError = e;
      }

      const remaining = SDID_POLL_TIMEOUT_MS - (Date.now() - startedAt);
      if (remaining <= 0) {
        hasTimeLeft = false;
      } else {
        // Cap sleep so we do not overshoot the timeout, then poll again (including
        // a final attempt after the last partial sleep near the deadline).
        await sleep(Math.min(SDID_POLL_MS, remaining));
      }
    }
  } catch (e) {
    lastError = e;
  }

  if (lastError !== undefined) {
    console.error('[getWebSingularSdid] failed:', lastError);
  }

  return undefined;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
